import { Loading } from '@/core/ui/components/Loading';

import React, { useState, useEffect } from 'react';
import { Calendar, Clock, CheckCircle, XCircle, HelpCircle, AlertTriangle } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { useDateFormatter } from '@/shared/hooks';
import { useServices, useToast } from '@/app/providers';
import { AmenityReservation, ReservationStatus, UserUnit } from '@/shared/types';

interface MyReservationListProps {
  userId: string;
  /** Unidades del residente — solo se usa para mostrar a qué casa pertenece cada reserva cuando tiene más de una. */
  units?: UserUnit[];
}

const getUnitLabel = (unit: UserUnit['unit']): string => {
  if (unit.name) return unit.name;
  if (unit.tower) return `Torre ${unit.tower} - ${unit.number || unit.floor || ''}`.trim();
  return `${unit.street || ''} #${unit.exteriorNumber || unit.number || ''}`.trim() || 'Unidad';
};

// ─── Confirmation Modal ───────────────────────────────────────────────────────

interface CancelConfirmModalProps {
  isOpen: boolean;
  amenityName: string;
  reservationDate: string;
  onConfirm: () => void;
  onClose: () => void;
  isLoading: boolean;
}

const CancelConfirmModal: React.FC<CancelConfirmModalProps> = ({
  isOpen,
  amenityName,
  reservationDate,
  onConfirm,
  onClose,
  isLoading,
}) => {
  const { t } = useI18n();

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4"
      style={{ backgroundColor: 'rgba(9, 9, 11, 0.6)', backdropFilter: 'blur(8px)' }}
      onClick={() => { if (!isLoading) onClose(); }}
    >
      {/* Panel */}
      <div
        className="relative w-full sm:max-w-[400px] bg-white rounded-t-[28px] sm:rounded-[20px] overflow-hidden flex flex-col"
        style={{ boxShadow: '0 24px 64px -8px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.06)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top glow bleed */}
        <div
          className="absolute inset-x-0 top-0 h-40 pointer-events-none"
          style={{ background: 'radial-gradient(60% 80px at 50% 0%, rgba(252,165,165,0.18) 0%, transparent 100%)' }}
        />

        {/* Mobile drag handle */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-9 h-1 rounded-full bg-zinc-200" />
        </div>

        <div className="relative px-7 pt-6 pb-8 flex flex-col gap-5">

          {/* Warning icon */}
          <div className="flex justify-center pt-1">
            <div
              className="w-[60px] h-[60px] rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(160deg, #fff1f2 0%, #ffe4e6 100%)',
                boxShadow: '0 2px 12px rgba(239,68,68,0.15), inset 0 1px 0 rgba(255,255,255,0.85)',
              }}
            >
              <AlertTriangle size={24} strokeWidth={2.2} style={{ color: '#e53e3e' }} />
            </div>
          </div>

          {/* Heading + body */}
          <div className="text-center flex flex-col gap-2">
            <h2
              className="text-[17px] font-semibold tracking-[-0.01em]"
              style={{ color: '#18181b' }}
            >
              {t('amenities.cancel.modal_title')}
            </h2>
            <p
              className="text-[13.5px] leading-[1.6] mx-auto max-w-[280px]"
              style={{ color: '#71717a' }}
              dangerouslySetInnerHTML={{
                __html: t('amenities.cancel.modal_body')
                  .replace('{{amenity}}', `<span style="color:#18181b;font-weight:600">${amenityName}</span>`)
                  .replace('{{date}}', `<span style="color:#18181b;font-weight:600">${reservationDate}</span>`)
              }}
            />
          </div>

          {/* Info chip */}
          <div
            className="flex items-center justify-center gap-2.5 rounded-xl px-4 py-2.5 text-[12.5px] font-medium mx-auto"
            style={{
              background: '#f4f4f5',
              border: '1px solid #e4e4e7',
              color: '#71717a',
              maxWidth: '280px',
              width: '100%',
            }}
          >
            <Calendar size={12} style={{ color: '#a1a1aa', flexShrink: 0 }} />
            <span className="truncate">{amenityName}</span>
            <span style={{ color: '#d4d4d8' }}>·</span>
            <Clock size={12} style={{ color: '#a1a1aa', flexShrink: 0 }} />
            <span>{reservationDate}</span>
          </div>

          {/* Divider */}
          <div style={{ height: '1px', background: '#f4f4f5' }} />

          {/* Action buttons */}
          <div className="flex gap-2.5">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 py-[11px] rounded-[13px] text-[13.5px] font-semibold transition-all
                         disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                color: '#3f3f46',
                background: '#f4f4f5',
                border: '1px solid #e4e4e7',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#ececed')}
              onMouseLeave={e => (e.currentTarget.style.background = '#f4f4f5')}
            >
              {t('amenities.cancel.keep')}
            </button>

            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 py-[11px] rounded-[13px] text-[13.5px] font-semibold text-white
                         flex items-center justify-center gap-2
                         transition-all disabled:opacity-55 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(160deg, #f87171 0%, #dc2626 100%)',
                boxShadow: '0 1px 0 rgba(255,255,255,0.14) inset, 0 4px 16px -4px rgba(220,38,38,0.45)',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'linear-gradient(160deg, #ef6060 0%, #c81e1e 100%)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'linear-gradient(160deg, #f87171 0%, #dc2626 100%)')}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-[14px] w-[14px]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3.5" />
                    <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  {t('common.cancelling', { defaultValue: 'Cancelling…' })}
                </>
              ) : (
                t('amenities.cancel.confirm')
              )}
            </button>
          </div>

          {/* iOS safe area spacer */}
          <div className="sm:hidden" style={{ height: 'env(safe-area-inset-bottom, 8px)' }} />
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

export const MyReservationList: React.FC<MyReservationListProps> = ({ userId, units }) => {
  const showUnitColumn = (units?.length ?? 0) > 1;
  const resolveUnitLabel = (res: AmenityReservation): string | null => {
    if (!res.unitId) return null;
    const assignment = units?.find((item) => item.unitId === res.unitId);
    return assignment ? getUnitLabel(assignment.unit) : null;
  };

  const { t } = useI18n();
  const { formatDate } = useDateFormatter();
  const { amenityReservationRepo } = useServices();
  const toast = useToast();
  const [reservations, setReservations] = useState<AmenityReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [modalTarget, setModalTarget] = useState<{ id: string; amenityName: string; date: string } | null>(null);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const response = await amenityReservationRepo.getMyReservations({ page: 1, limit: PAGE_SIZE });
        setReservations(response.data);
        setHasNextPage(response.meta.hasNextPage);
        setPage(1);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchInitial();
  }, [userId, amenityReservationRepo]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasNextPage) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const response = await amenityReservationRepo.getMyReservations({ page: nextPage, limit: PAGE_SIZE });
      setReservations((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...response.data.filter((r) => !seen.has(r.id))];
      });
      setHasNextPage(response.meta.hasNextPage);
      setPage(nextPage);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  };

  const getAmenityName = (res: AmenityReservation) =>
    res.amenity?.name ?? res.memberName ?? res.amenityId;

  const getStatusProps = (status: string) => {
    const s = status.toLowerCase();
    switch (s) {
      case 'confirmed':
        return {
          icon: <CheckCircle size={11} strokeWidth={2.5} />,
          style: { color: '#059669', background: '#ecfdf5', border: '1px solid #6ee7b7' },
          text: t(`amenities.status.${s}`),
        };
      case 'pending':
        return {
          icon: <Clock size={11} strokeWidth={2.5} />,
          style: { color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d' },
          text: t(`amenities.status.${s}`),
        };
      case 'cancelled':
        return {
          icon: <XCircle size={11} strokeWidth={2.5} />,
          style: { color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5' },
          text: t(`amenities.status.${s}`),
        };
      default:
        return {
          icon: <HelpCircle size={11} strokeWidth={2.5} />,
          style: { color: '#71717a', background: '#fafafa', border: '1px solid #e4e4e7' },
          text: status,
        };
    }
  };

  const requestCancel = (res: AmenityReservation) => {
    setModalTarget({
      id: res.id,
      amenityName: getAmenityName(res),
      date: formatDate(res.date),
    });
  };

  const handleConfirmCancel = async () => {
    if (!modalTarget) return;
    const { id } = modalTarget;
    setCancelling(id);
    try {
      await amenityReservationRepo.cancelReservation(id);
      setReservations(prev =>
        prev.map(r => r.id === id ? { ...r, status: ReservationStatus.CANCELLED } : r)
      );
      setModalTarget(null);
    } catch (error) {
      console.error('Failed to cancel reservation', error);
      toast.error(t('amenities.cancel.error_message'), t('amenities.errors.title'));
    } finally {
      setCancelling(null);
    }
  };

  const handleCloseModal = () => {
    if (cancelling) return;
    setModalTarget(null);
  };

  if (loading) return <Loading size="sm" />;

  if (reservations.length === 0) {
    return (
      <div
        className="rounded-2xl p-12 text-center"
        style={{ background: '#fafafa', border: '1.5px dashed #e4e4e7' }}
      >
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
          style={{ background: '#f4f4f5' }}
        >
          <Calendar size={20} strokeWidth={1.5} style={{ color: '#a1a1aa' }} />
        </div>
        <p className="text-sm font-medium" style={{ color: '#a1a1aa' }}>{t('amenities.list.empty')}</p>
      </div>
    );
  }

  const columns = [
    t('amenities.list.amenity'),
    t('amenities.list.date'),
    t('amenities.list.cost'),
    t('amenities.list.status'),
    t('amenities.list.actions'),
  ];

  return (
    <>
      <CancelConfirmModal
        isOpen={!!modalTarget}
        amenityName={modalTarget?.amenityName ?? ''}
        reservationDate={modalTarget?.date ?? ''}
        onConfirm={handleConfirmCancel}
        onClose={handleCloseModal}
        isLoading={!!cancelling}
      />

      <div
        className="bg-white overflow-hidden"
        style={{
          borderRadius: '16px',
          border: '1px solid #e4e4e7',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 4px 16px -4px rgba(0,0,0,0.06)',
        }}
      >
        {/* Card header */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: '1px solid #f4f4f5' }}
        >
          <h3 className="text-sm font-semibold tracking-tight" style={{ color: '#18181b' }}>
            {t('amenities.my_reservations')}
          </h3>
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{ background: '#f4f4f5', color: '#71717a' }}
          >
            {reservations.length}
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left" style={{ fontSize: '13.5px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f4f4f5' }}>
                {columns.map((col) => (
                  <th
                    key={col}
                    className="px-5 py-3 text-[11px] font-semibold uppercase tracking-widest"
                    style={{ color: '#a1a1aa', background: '#fafafa' }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reservations.map((res, idx) => {
                const { icon, style, text } = getStatusProps(res.status);
                const canBeCancelled = ['CONFIRMED', 'PENDING'].includes(res.status);
                const isCancelling = cancelling === res.id;
                const isLast = idx === reservations.length - 1;

                return (
                  <tr
                    key={res.id}
                    className="group transition-colors"
                    style={{ borderBottom: isLast ? 'none' : '1px solid #f4f4f5' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    {/* Amenity name */}
                    <td className="px-5 py-4">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-semibold" style={{ color: '#18181b' }}>
                          {getAmenityName(res)}
                        </span>
                        {showUnitColumn && resolveUnitLabel(res) && (
                          <span style={{ fontSize: '11.5px', color: '#a1a1aa' }}>
                            {resolveUnitLabel(res)}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Date + time */}
                    <td className="px-5 py-4">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium" style={{ color: '#3f3f46' }}>
                          {formatDate(res.date)}
                        </span>
                        <span
                          className="flex items-center gap-1"
                          style={{ fontSize: '11.5px', color: '#a1a1aa' }}
                        >
                          <Clock size={10} strokeWidth={2} />
                          {res.startTime}
                          <span style={{ color: '#d4d4d8' }}>·</span>
                          {res.hours}h
                        </span>
                      </div>
                    </td>

                    {/* Cost */}
                    <td className="px-5 py-4">
                      <span
                        className="font-semibold tabular-nums"
                        style={{ color: '#18181b', fontVariantNumeric: 'tabular-nums' }}
                      >
                        ${res.totalCost}
                      </span>
                    </td>

                    {/* Status badge */}
                    <td className="px-5 py-4">
                      <span
                        className="inline-flex items-center gap-1.5 font-semibold rounded-full px-2.5 py-[4px]"
                        style={{ ...style, fontSize: '11.5px' }}
                      >
                        {icon}
                        {text}
                      </span>
                    </td>

                    {/* Cancel action */}
                    <td className="px-5 py-4">
                      {canBeCancelled && (
                        <button
                          onClick={() => requestCancel(res)}
                          disabled={isCancelling}
                          className="font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ fontSize: '12px', color: '#dc2626' }}
                          onMouseEnter={e => { if (!isCancelling) e.currentTarget.style.color = '#991b1b'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = '#dc2626'; }}
                        >
                          {isCancelling
                            ? t('common.cancelling', { defaultValue: 'Cancelling…' })
                            : t('common.cancel', { defaultValue: 'Cancel' })}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {hasNextPage && (
          <div
            className="flex items-center justify-center px-5 py-4"
            style={{ borderTop: '1px solid #f4f4f5' }}
          >
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="inline-flex items-center gap-2 font-semibold rounded-full px-4 py-2 transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                fontSize: '12.5px',
                color: '#3f3f46',
                background: '#fafafa',
                border: '1px solid #e4e4e7',
              }}
              onMouseEnter={e => { if (!loadingMore) e.currentTarget.style.background = '#f4f4f5'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fafafa'; }}
            >
              {loadingMore ? (
                <>
                  <svg
                    className="animate-spin h-3.5 w-3.5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3.5" />
                    <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  {t('common.loadingMore')}
                </>
              ) : (
                t('common.loadMore')
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};